// @flow

import React, { FC, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import VersionForm from './VersionForm';
import EmailEditorBlock from './EmailEditorBlock';
import { getInitVersion } from './functions';
import { checkPermission } from '../../utils/checkPermission';
import { checkFormValidity, getEditData } from '../../utils/formFunctions';
import translate from '@translate';
import Grid, { GridItem } from '../../components/Grid';
import Typography from '../../components/Typography';
import ConfirmDialog from '../../components/ConfirmDialog';
import changeFormValue from 'src/utils/changeFormValue';
import TestEmail from './TestEmail';
import TemplateForm from './TemplateForm';
import {
    activateEmailTemplateVersionDetail,
    clearEmailTemplateDetail, createEmailTemplateDetail,
    createEmailTemplateVersionDetail,
    deleteEmailTemplateDetail, deleteEmailTemplateVersionDetail,
    editEmailTemplateDetail,
    editEmailTemplateVersionDetail,
    getEmailTemplateDetailFetch,
    testEmailTemplateVersionDetail
} from '../../resources/emailTemplateDetail/reducer';
import { RootStoreType } from '../../types/store';
import { EmailTemplateType } from '../../types/emailTemplate';
import { addMessage } from '../../resources/alertMessages/reducer';
import { setLocationParams } from '../../resources/location/reducer';
import { FormErrorsType, OnChangeType } from '../../types/global';

type EditKeysType = 'NAME' | 'LANG' | 'TYPE';

const initData: Record<string, any> = {};
const initVersionData: Record<string, any> = {};

export interface EmailTemplateDetailContainerProps {
    newEmailTemplate?: any;
    getEmailTemplateDetail?: any;
    clearEmailTemplateDetail?: any;
    deleted?: any;
    emailTemplateData?: any;
    deleteEmailTemplateVersion?: any;
    activateEmailTemplateVersion?: any;
    addMessage?: any;
    createEmailTemplate?: any;
    editEmailTemplateVersion?: any;
    createEmailTemplateVersion?: any;
    deleteEmailTemplate?: any;
    testEmailTemplateVersion?: any;
    templateDataStructureCodes?: any;
    templateDataStructureCodesObject?: any;
}

export const EDIT_KEYS = ['NAME', 'LANG', 'TYPE'];

type ConfigParamsType = {
    title: string,
    content?: string;
    customContent?: any;
    action: any;
    onCancel?: any;
    onClose?: any;
};
const EmailTemplateDetailContainer: FC<EmailTemplateDetailContainerProps> = (props) => {
    const dispatch = useDispatch();

    const { idEmailTemplate } = useParams();
    const formDetailRef = useRef<HTMLFormElement>(null);
    const formVersionRef = useRef<HTMLFormElement>(null);
    const navigate = useNavigate();

    const data = useSelector((state: RootStoreType) => state.emailTemplateDetail.data);
    const [editData, setEditData] = useState<EmailTemplateType>(getEditData(initData, EDIT_KEYS));
    const [editErrors, setEditErrors] = useState<FormErrorsType>({});
    const [versionData, setVersionData] = useState(initVersionData);
    const [versionErrors, setVersionErrors] = useState({});
    const [confirmParams, setConfirmParams] = useState<ConfigParamsType | null>(null);

    const backUrl = '/admin/email_templates/';

    useEffect(() => {
        if (idEmailTemplate) {
            dispatch(setLocationParams({ name: translate('emailTemplateDetail.title', { idEmailTemplate }), backUrl }));
        } else {
            dispatch(setLocationParams({ name: translate('emailTemplateDetail.titleNew'), backUrl }));
        }
    }, [idEmailTemplate, backUrl]);

    // laod data, on close clear store
    useEffect((): any => {
        if (idEmailTemplate) {
            dispatch(getEmailTemplateDetailFetch(idEmailTemplate));
        }
        return () => {
            dispatch(clearEmailTemplateDetail());
        };
    }, [idEmailTemplate]);

    // close if deleted tempalte
    useEffect((): any => {
        if (props.deleted) {
            handleClose();
        }
    }, [props.deleted]);

    // reset after reload to fresh data
    useEffect(() => {
        if (data) {
            setEditData(data);
            if (props.newEmailTemplate && data.ID) {
                dispatch(clearEmailTemplateDetail());
                navigate(`/admin/email_templates/${data.ID}`);
            }

            // set new, sleected, active or first version selected
            updateSelectVersion(getInitVersion(data, data, versionData));
        }
    }, [data]);

    // update select version
    const updateSelectVersion = (version: any) => {
        if (!version?.id) {
            setVersionData({ ...initVersionData });
        } else if (version) {
            setVersionData({ ...(version || initVersionData) });
        }
    };

    const disableEdit = props.newEmailTemplate
        ? !checkPermission('EMAIL_TEMPLATE.CREATE')
        : !checkPermission('EMAIL_TEMPLATE.UPDATE');

    const handleClose = () => {
        navigate('/admin/email_templates');
    };

    const changeDataValue: any = (value: any, dataKey: any, error: any) => {
        const newData = changeFormValue(value, dataKey, error, data, editErrors);

        if (newData.errors) {
            setEditErrors(newData.errors);
        }
        setEditData(newData.data);
    };

    const onChange: OnChangeType<EditKeysType> = (key, value, error) => {
        const newData = { ...editData };
        const newErrors = { ...editErrors };
        // @ts-ignore
        newData[key] = value;
        newErrors[key] = error;
        setEditData(newData);
        setEditErrors(newErrors);
    };

    const changeVersionValue: any = (value: any, dataKey: any, error: any) => {
        const newData = changeFormValue(value, dataKey, error, versionData, versionErrors);

        if (newData.errors) {
            setVersionErrors(newData.errors);
        }
        setVersionData(newData.data);
    };

    const deleteVersion = () => {
        confirmVersion(() => {
                dispatch(deleteEmailTemplateVersionDetail({ idEmailTemplate, idVersion: versionData.id }));
            }
        );
    };

    const activateVersion = () => {
        confirmVersion(() => dispatch(activateEmailTemplateVersionDetail({ idEmailTemplate, idVersion: versionData.id })));
    };

    const validateTemplate = () => {
        // @ts-ignore
        const newErrors = checkFormValidity(formDetailRef);
        const hasErrors = Object.keys(newErrors).length > 0;
        if (hasErrors) {
            dispatch(
                addMessage({
                    type: 'warning',
                    title: translate('messages.formWarningTitle'),
                    text: translate('messages.formWarningText')
                })
            );
        }
        setEditErrors(newErrors || {});

        return !hasErrors;
    };

    const saveTemplate = () => {
        if (validateTemplate()) {
            const saveData = {
                LANG: editData?.LANG,
                NAME: editData?.NAME,
                TYPE: editData?.TYPE
            };
            if (!props.newEmailTemplate) {
                confirmTemplate(() =>
                    dispatch(
                        editEmailTemplateDetail({
                            idEmailTemplate,
                            data: { ...saveData, active: 0 }
                        })
                    ));
            } else if (validateVersion()) {
                dispatch(createEmailTemplateDetail({ template: saveData, version: getSaveVersionData(true) }));
            }
        }
    };

    const getSaveVersionData = (create?: any) => {
        const saveVersionData = {
            active: versionData?.active,
            name: versionData?.name,
            test_data: versionData?.test_data,
            subject: versionData?.subject,
            html_content: versionData?.html_content
        };
        if (create) {
            saveVersionData.active = 0;
        }
        return { ...saveVersionData };
    };

    const validateVersion = () => {
        // @ts-ignore
        const newErrors = checkFormValidity(formVersionRef);
        const hasErrors = Object.keys(newErrors).length > 0;

        if (hasErrors) {
            dispatch(
                addMessage({
                    type: 'warning',
                    title: translate('messages.formWarningTitle'),
                    text: translate('messages.formWarningText')
                })
            );
        }
        setVersionErrors(newErrors || {});

        return !hasErrors;
    };

    const confirmVersion = (callback: any) => {
        // @ts-ignore
        setConfirmParams({
            title: translate('emailTemplateDetail.confirmSaveVersionTitle'),
            content: translate('emailTemplateDetail.confirmSaveVersionContent'),
            action: () => {
                callback();
            }
        });
    };

    const confirmTemplate = (callback: any) => {
        // @ts-ignore
        setConfirmParams({
            title: translate('emailTemplateDetail.confirmSaveTemplateTitle'),
            content: translate('emailTemplateDetail.confirmSaveTemplateContent'),
            action: () => {
                callback();
            }
        });
    };

    const saveVersion = (newVersion: any) => {
        if (validateVersion()) {
            confirmVersion(() => {
                if (!newVersion) {
                    dispatch(
                        editEmailTemplateVersionDetail({
                            idEmailTemplate,
                            idVersion: versionData?.id,
                            data: getSaveVersionData()
                        })
                    );
                } else {
                    dispatch(
                        createEmailTemplateVersionDetail({
                            idEmailTemplate,
                            data: getSaveVersionData(true)
                        })
                    );
                }
            });
        }
    };

    const deleteTemplate = () => {
        confirmTemplate(() => {
            dispatch(deleteEmailTemplateDetail({ idEmailTemplate }))
            navigate('/admin/email_templates');
        });
    };

    const duplicateTemplate = () => {
        dispatch(
            clearEmailTemplateDetail()
        );
        setVersionData({
            html_content: versionData?.html_content,
            test_data: versionData?.test_data
        });
        setEditData(initData);
        navigate('/admin/email_templates/new');
    };

    const testVersion = () => {
        setConfirmParams({
            title: translate('emailTemplateDetail.sendTestEmail'),
            customContent: (emailData: any, setEmailData: any): any => {
                return <TestEmail data={emailData} setData={setEmailData} />;
            },
            action: (emailData: any) => {
                dispatch(
                    testEmailTemplateVersionDetail({
                        idEmailTemplate,
                        idVersion: versionData.id,
                        data: {
                            email_to: emailData.data.email,
                            test_data: versionData.test_data
                        }
                    })
                );
            }
        });
    };

    return (
        <div>
            {/*<Detail*/}
            {/*    title={*/}
            {/*        props.newEmailTemplate*/}
            {/*            ? translate('emailTemplateDetail.titleNew')*/}
            {/*            : translate('emailTemplateDetail.title', { idEmailTemplate })*/}
            {/*    }*/}
            {/*    onClose={handleClose}*/}
            {/*    historyBackUrl="/admin/emailTemplates"*/}
            {/*    loading={props.loading}*/}
            {/*    maxWidth="xl"*/}
            {/*>*/}
            <Grid>
                <GridItem cols={6}>
                    <Typography variant='h6' gutterBottom>
                        {translate('emailTemplateDetail.template')}:
                    </Typography>
                    <form ref={formDetailRef}>
                        <TemplateForm
                            data={editData}
                            errors={editErrors}
                            newEmailTemplate={props.newEmailTemplate}
                            changeValue={onChange}
                            disableEdit={disableEdit}
                            saveTemplate={saveTemplate}
                            duplicateTemplate={duplicateTemplate}
                            deleteTemplate={deleteTemplate}
                            templateDataStructureCodes={props.templateDataStructureCodes}
                            templateDataStructureCodesObject={props.templateDataStructureCodesObject}
                        />
                    </form>
                </GridItem>
                <GridItem cols={6}>
                    <Typography variant='h6' gutterBottom>
                        {translate('emailTemplateDetail.version')}:
                    </Typography>
                    <form ref={formVersionRef}>
                        <VersionForm
                            data={versionData}
                            errors={versionErrors}
                            newEmailTemplate={props.newEmailTemplate}
                            emailTemplateData={props.emailTemplateData}
                            changeValue={changeVersionValue}
                            disableEdit={disableEdit}
                            updateSelectVersion={updateSelectVersion}
                            saveVersion={saveVersion}
                            activateVersion={activateVersion}
                            deleteVersion={deleteVersion}
                            testVersion={testVersion}
                            changeVersionValue={changeVersionValue}
                        />
                    </form>
                </GridItem>
            </Grid>

            {confirmParams && (
                <ConfirmDialog
                    title={confirmParams.title}
                    customContent={confirmParams.customContent}
                    content={confirmParams.content}
                    action={confirmParams.action}
                    onCancel={confirmParams.onCancel}
                    maxWidth='md'
                    onClose={(): any => {
                        if (confirmParams.onClose) {
                            confirmParams.onClose();
                        }
                        setConfirmParams(null);
                    }}
                />
            )}
            {/*</Detail>*/}
        </div>
    );
};

export default EmailTemplateDetailContainer;
